import { FC } from 'react';
import { Box, Stack, Theme, Tooltip, Typography } from '@mui/material';

import { bm } from '@/theme/bausteinMasse';

/**
 * Design-System Teil B (DESIGN_SYSTEM.md, Variante A):
 * Reiter-Leiste im Pill-Stil. Aktiver Tab ist gefüllt (primary.main),
 * inaktive Tabs sind transparent. Optionaler Count als dezenter Suffix.
 *
 * Design-Test Stufe 2 (25.10.2026): additive `variante="unterstrich"` —
 * reine Textreiter, aktiv = 2px Unterstrich in Primary, Leiste mit Hairline
 * darunter. Default bleibt `pille`, damit alle anderen Screens unveraendert
 * bleiben; heute schaltet nur die Fall-Ansicht um.
 *
 * Nachtrag Alex 29.10.2026: additive `variante="segment"` — Umschalter
 * zwischen wenigen Werten (Konto-Menue „Darstellung": Hell / Dunkel / System).
 * Farben wie `pille`, aber 8-px-Ecken wie die Knoepfe (`bausteinMasse.knopf.radius`)
 * statt der runden Pillenform, die nicht zum Zielstil passt (Decision #246).
 */
export interface TabPill {
  key: string;
  label: string;
  count?: number;
  /**
   * Erklaerung am Reiter (Cockpit-Terminverteilung, Alex 06.10.2026: der
   * Tooltip nennt die Klasse der EB-Verteilung). Additiv — ohne das Feld
   * rendert der Reiter wie bisher, ohne Tooltip-Huelle.
   */
  tooltip?: string;
}

export interface TabPillsProps {
  tabs: TabPill[];
  activeKey: string;
  onChange: (key: string) => void;
  'aria-label'?: string;
  variante?: 'pille' | 'unterstrich' | 'segment';
}

const pilleSx = (isActive: boolean, segment = false) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  px: '12px',
  py: '6px',
  border: 'none',
  borderRadius: segment ? (t: Theme) => bm(t).knopf.radius : '999px',
  cursor: 'pointer',
  fontFamily: 'inherit',
  typography: isActive ? 'wertBetont' : 'wert',
  color: isActive ? 'primary.contrastText' : 'text.primary',
  bgcolor: isActive ? 'primary.main' : 'transparent',
  transition: 'background-color 0.15s',
  '&:hover': {
    bgcolor: isActive ? 'primary.main' : 'background.card',
  },
  '&:focus-visible': {
    outline: '2px solid',
    outlineColor: 'primary.main',
    outlineOffset: 2,
  },
});

const unterstrichSx = (isActive: boolean) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 2,
  px: 0,
  py: 4,
  mb: '-1px',
  border: 'none',
  borderBottom: '2px solid',
  borderBottomColor: isActive ? 'primary.main' : 'transparent',
  bgcolor: 'transparent',
  cursor: 'pointer',
  fontFamily: 'inherit',
  typography: 'wertBetont',
  color: isActive ? 'primary.main' : 'text.primary',
  transition: 'color 150ms, border-color 150ms',
  '&:hover': {
    color: isActive ? 'primary.main' : 'text.secondary',
  },
  '&:focus-visible': {
    outline: '2px solid',
    outlineColor: 'primary.main',
    outlineOffset: 2,
  },
});

const TabPills: FC<TabPillsProps> = ({
  tabs, activeKey, onChange, 'aria-label': ariaLabel, variante = 'pille',
}) => {
  const unterstrich = variante === 'unterstrich';
  return (
    <Stack
      direction="row"
      role="tablist"
      aria-label={ariaLabel}
      sx={unterstrich
        ? { gap: 6, flexWrap: 'wrap' }
        : { gap: '6px', flexWrap: 'wrap' }}
    >
      {tabs.map((tab) => {
        const isActive = tab.key === activeKey;
        const reiter = (
          <Box
            key={tab.key}
            component="button"
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(tab.key)}
            sx={unterstrich ? unterstrichSx(isActive) : pilleSx(isActive, variante === 'segment')}
          >
            <Typography
              component="span"
              sx={unterstrich
                ? { font: 'inherit', color: 'inherit', lineHeight: 1 }
                : { font: 'inherit', color: 'inherit', lineHeight: 1 }}
            >
              {tab.label}
            </Typography>
            {typeof tab.count === 'number' && (
              <Typography
                component="span"
                // Rolle als variant — `sx.typography` wuerde an einer
                // Typography das geerbte Weiss der aktiven Pille ueberstimmen.
                variant={unterstrich ? 'klein' : 'label'}
                sx={unterstrich
                  ? { lineHeight: 1 }
                  : {
                    lineHeight: 1,
                    opacity: isActive ? 0.85 : 0.75,
                    color: 'inherit',
                  }}
              >
                {tab.count.toLocaleString('de-DE')}
              </Typography>
            )}
          </Box>
        );

        return tab.tooltip
          ? <Tooltip key={tab.key} title={tab.tooltip} arrow describeChild>{reiter}</Tooltip>
          : reiter;
      })}
    </Stack>
  );
};

export default TabPills;
